'use client';

import { motion } from 'framer-motion';
import React, { useRef, useCallback } from 'react';
import { Button } from '@/components/common/inputs/button/button';
import { Textarea } from '@/components/common/inputs/textarea/textarea';
import { useAutosizeTextArea } from '@/lib/hooks';
import { cn } from '@/lib/utils';
import { ClipboardListIcon, XIcon } from 'lucide-react';
import { useState } from 'react';
import { useFormStore } from '@/lib/hooks/useFormStore';
import QuestionnaireAnswerDisplay from '@/components/features/questionnaire/components/QuestionnaireAnswerDisplay';
import { useScopedI18n } from '@/locales/client';

export interface TextareaWithButtonProps {
  shouldTextAreaBeAvailable?: boolean;
  inputValue: string;
  setInputValue: (value: string) => void;
  handlePromptSubmission: (promptText: string, formAnswerId?: string) => void;
  handleFormGoBack: () => void;
  formAnswerId?: string;
  t?: (key: string) => string;
  isLoading?: boolean;
  promptInputRef?: React.RefObject<HTMLTextAreaElement>;
  isDisabled?: boolean;
}

const TextareaWithButton: React.FC<TextareaWithButtonProps> = ({
  shouldTextAreaBeAvailable = true,
  inputValue,
  setInputValue,
  handlePromptSubmission,
  handleFormGoBack,
  formAnswerId = '',
  t = (key: string) => key,
  isLoading = false,
  promptInputRef: externalRef,
  isDisabled = false,
}) => {
  const internalRef = useRef<HTMLTextAreaElement>(null);
  const textareaRef = externalRef || internalRef;
  const isTextInInput = inputValue.trim().length > 0;

  const tForm = useScopedI18n('formQuestionnaire');

  useAutosizeTextArea(textareaRef.current, inputValue);

  // Memoize the submission handler.
  const handleSubmit = useCallback(() => {
    if (!isTextInInput || isLoading) return;
    handlePromptSubmission(inputValue, formAnswerId);
  }, [
    isTextInInput,
    isLoading,
    handlePromptSubmission,
    inputValue,
    formAnswerId,
  ]);

  // const [showFormAnswers, setShowFormAnswers] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { formData } = useFormStore(); // Fetch form answers

  const MAX_LENGTH = 1000;

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (e.target.value.length <= MAX_LENGTH) {
      setInputValue(e.target.value);
    } else {
      setInputValue(e.target.value.slice(0, 1000));
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className='relative flex w-full items-center shadow-lg'
    >
      {/* Questionnaire Icon on the left */}
      <button
        onClick={() => setIsModalOpen(true)}
        className='absolute left-3 top-1/2 -translate-y-1/2 text-purple-600 transition-all hover:scale-110 hover:text-purple-800 focus:outline-none'
      >
        <ClipboardListIcon className='h-5 w-5' />
      </button>
      {/* Dropdown with form answers */}
      {/* Modal */}
      {isModalOpen && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className='fixed inset-0 z-10 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm'
        >
          <div className='relative w-full max-w-[520px] rounded-2xl border bg-white p-6 shadow-xl dark:border-gray-700 dark:bg-gray-900'>
            {/* Close Button */}
            <button
              onClick={() => setIsModalOpen(false)}
              className='absolute right-4 top-4 text-gray-400 transition hover:text-gray-600 dark:hover:text-gray-300'
            >
              <XIcon className='h-5 w-5' />
            </button>

            {/* Modal Header */}
            <div className='px-4 sm:px-0'>
              <h3 className='text-base/7 font-semibold text-gray-900 dark:text-white'>
                {tForm('viewAnswers.title')}
              </h3>
            </div>
            {/* Content */}
            <div className='text-center'>
              <QuestionnaireAnswerDisplay answers={formData?.answers || {}} />
            </div>

            {/* Action Buttons */}
            <div className='mt-6 flex justify-between'>
              <button
                onClick={() => setIsModalOpen(false)}
                className='rounded-lg px-4 py-2 text-sm font-semibold text-gray-600 transition hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
              >
                {tForm('viewAnswers.cancel')}
              </button>
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  handleFormGoBack();
                }}
                className='rounded-lg bg-purple-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-purple-700'
              >
                {tForm('viewAnswers.modify')}
              </button>
            </div>
          </div>
        </motion.div>
      )}

      <Textarea
        onChange={handleChange}
        value={inputValue}
        ref={textareaRef}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
          }
        }}
        placeholder={t('placeholders.askQuestion')}
        disabled={!shouldTextAreaBeAvailable || isLoading}
        className={cn(
          isTextInInput ? '' : '!h-[24px]',
          'hide-scrollbar rounded-lg p-4 pl-10 pr-14', // Added pl-10 for space for the settings icon
          'focus:border-purple-600',
          'bg-white dark:bg-gray-900'
        )}
      />

      <Button
        onClick={handleSubmit}
        disabled={!isTextInInput || isLoading}
        className={cn(
          'absolute right-2 top-1/2 h-8 w-8 -translate-y-1/2 rounded-full p-0',
          isTextInInput && !isLoading
            ? 'bg-gradient-to-l from-purple-400 to-purple-600 text-white hover:from-purple-500 hover:to-purple-700 dark:from-purple-500 dark:to-purple-700 dark:hover:from-purple-600 dark:hover:to-purple-800'
            : 'bg-gray-100 text-gray-400 dark:bg-gray-900/95 dark:text-purple-200/30'
        )}
      >
        {isLoading ? (
          <div className='h-4 w-4 animate-spin rounded-full border-2 border-gray-200/30 border-t-purple-600 dark:border-purple-400/20 dark:border-t-purple-300' />
        ) : (
          <svg
            xmlns='http://www.w3.org/2000/svg'
            viewBox='0 0 16 16'
            fill='none'
            className='h-4 w-4'
          >
            <path
              d='M.5 1.163A1 1 0 0 1 1.97.28l12.868 6.837a1 1 0 0 1 0 1.766L1.969 15.72A1 1 0 0 1 .5 14.836V10.33a1 1 0 0 1 .816-.983L8.5 8 1.316 6.653A1 1 0 0 1 .5 5.67V1.163Z'
              fill='currentColor'
            />
          </svg>
        )}
      </Button>
    </motion.div>
  );
};

export default TextareaWithButton;
